import { getColumnDynamicsState, isColDef, isColGroupDef, type Column, type ColumnDynamics } from '@talos/kyoko';
import {
  createGetPriceGridMarketDataRequestParams,
  getPriceGridMarketDataColumnGroup,
  PRICE_GRID_MARKET_DATA_COLLECTION_PATH,
  PRICE_GRID_MARKET_DATA_COLUMN_INITIAL_WIDTH,
  PRICE_GRID_MARKET_DATA_COLUMN_TYPE,
  type PriceGridLadderColumnConfig,
} from '../../columns';
import type { PriceGridSecurityMarketDataColumnParams } from '../../columns/types';
import { getPriceGridMarketDataPriceActions } from '../../hooks/actions/common/getPriceGridMarketDataPriceActions';
import type { PriceGridCommonActionsCellRendererParams } from '../../hooks/actions/common/types';
import { DEFAULT_PRICE_GRID_PRICE_INCREMENT, PriceGridLadderType } from '../../ladder';
import type { PriceGridEntity } from '../../PriceGridEntity';
import { PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC } from './constants';
import type {
  PriceGridLegMarketDataColumnSettings,
  PriceGridMarketDataColumnRow,
  SavePriceGridMarketDataColumnSettingsParams,
} from './types';
import {
  buildPriceGridMarketDataColumnColDef,
  getLegMarketDataColumnField,
  getMarketsFromSelectorValue,
} from './utils';

export function getPriceGridLegMarketDataColumn(
  dynamics: ColumnDynamics<PriceGridLegMarketDataColumnSettings>,
  handlers?: PriceGridCommonActionsCellRendererParams,
  {
    includeActionColumns,
    includeLegMarketSelectorColumn,
    includeRFQColumn,
    isBBOEnabled,
    ladderConfig = { ladderType: PriceGridLadderType.Ladder, priceIncrement: DEFAULT_PRICE_GRID_PRICE_INCREMENT },
  }: {
    includeActionColumns?: boolean;
    includeLegMarketSelectorColumn?: boolean;
    includeRFQColumn?: boolean;
    isBBOEnabled: boolean;
    ladderConfig?: PriceGridLadderColumnConfig;
  } = {
    isBBOEnabled: false,
  }
) {
  const settings = getColumnDynamicsState(dynamics);
  const column = {
    type: PRICE_GRID_MARKET_DATA_COLUMN_TYPE,
    field: getLegMarketDataColumnField(settings?.legIndex),
    title: settings?.title ?? 'Leg Price',
    id: dynamics.colID,
    initialWidth: PRICE_GRID_MARKET_DATA_COLUMN_INITIAL_WIDTH,
    params: {
      marketDataCollectionPath: PRICE_GRID_MARKET_DATA_COLLECTION_PATH satisfies keyof PriceGridEntity,
      Markets: getMarketsFromSelectorValue(settings?.marketSelectorValue),
      getRequestParams: createGetPriceGridMarketDataRequestParams(ladderConfig),
      sizeBucketField: 'bucket.size',
      getPriceActions: getPriceGridMarketDataPriceActions,
      ...handlers,
    } satisfies PriceGridSecurityMarketDataColumnParams,
    dynamics,
  } satisfies Column<
    PriceGridSecurityMarketDataColumnParams,
    PriceGridMarketDataColumnRow,
    PriceGridLegMarketDataColumnSettings
  >;

  return getPriceGridMarketDataColumnGroup({
    column,
    includeActionColumns,
    includeLegMarketSelectorColumn,
    includeRFQColumn,
    isBBOEnabled,
    ladderConfig,
  });
}

export function getPriceGridLegMarketDataColumnDynamics({
  colID,
  title,
  legIndex,
  removable = false,
}: {
  colID: string;
  title: string;
  legIndex?: number;
  removable?: boolean;
}): ColumnDynamics<PriceGridLegMarketDataColumnSettings> {
  return {
    colID,
    spec: PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
    removable,
    initialState: {
      title,
      legIndex,
    },
  };
}

export function savePriceGridLegMarketDataColumnSettings({
  api,
  colId,
  dynamics,
  newSettings,
  getColumn,
  getColDefOverlay,
}: SavePriceGridMarketDataColumnSettingsParams<PriceGridLegMarketDataColumnSettings>) {
  const columnDefs = api.getColumnDefs();
  if (!columnDefs) {
    return;
  }

  const targetColId = colId ?? dynamics.colID;
  const nextColDef = buildPriceGridMarketDataColumnColDef({
    getColumn,
    dynamics,
    newSettings,
    getColDefOverlay,
  });

  api.setGridOption(
    'columnDefs',
    columnDefs.map(colDef => {
      if (
        (isColDef(colDef) && colDef.colId === targetColId) ||
        (isColGroupDef(colDef) && colDef.groupId === dynamics.colID)
      ) {
        return nextColDef;
      }

      return colDef;
    })
  );
}
